import React from "react";

import Button from "../../../Components/Button";
import CommonModal from "../../../Components/Modal";

export interface KpiCompareMissingDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Remove accounts without KPI data from selection and open compare. */
  onRemoveAndCompare: () => void;
  accountNames: string[];
  /** True when every selected account lacks KPI data. */
  allSelectedLackData?: boolean;
}

/** Warns when Compare includes accounts without KPI data. */
const KpiCompareMissingDataModal: React.FC<KpiCompareMissingDataModalProps> = ({
  isOpen,
  onClose,
  onRemoveAndCompare,
  accountNames,
  allSelectedLackData = false,
}) => {
  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onClose}
      size="md"
      usePortal
      title="KPIs data not available"
      footer={
        <div className="flex w-full flex-wrap items-center justify-end gap-2">
          {allSelectedLackData ? (
            <Button variant="primary" onClick={onClose}>
              OK
            </Button>
          ) : (
            <>
              <Button variant="outline-primary" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" onClick={onRemoveAndCompare}>
                Remove & Compare
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="space-y-3 px-1 py-1 text-left">
        {allSelectedLackData ? (
          <p className="mb-0 text-sm leading-relaxed text-gray-600">
            None of the selected accounts have KPIs data. Please select accounts
            that have KPIs data to continue with compare.
          </p>
        ) : (
          <p className="mb-0 text-sm leading-relaxed text-gray-600">
            Some of the selected accounts don&apos;t have KPIs data. Please
            unselect those accounts to continue.
          </p>
        )}

        {accountNames.length > 0 ? (
          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Selected accounts without data
            </p>
            <ul className="m-0 max-h-40 list-disc space-y-1 overflow-y-auto pl-5 text-sm text-slate-700">
              {accountNames.map(name => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {!allSelectedLackData ? (
          <p className="mb-0 text-sm font-medium leading-relaxed text-slate-800">
            Do you want to remove the accounts without data and proceed with the
            accounts that have data for compare?
          </p>
        ) : null}
      </div>
    </CommonModal>
  );
};

export default KpiCompareMissingDataModal;

/** Accounts in the selection that have no Enterprise KPI data. */
export const getSelectedAccountsWithoutKpiData = <
  T extends { account_id: string; account_name: string; hasData?: boolean },
>(
  selectedAccounts: T[]
): T[] => selectedAccounts.filter(account => account.hasData === false);
